import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';

/**
 * OAuth Callback Handler
 * 
 * This page handles the redirect after OAuth sign-in (Google, Apple).
 * It reads the session token from the URL query params, stores it in localStorage,
 * then checks the user's onboarding status and redirects accordingly.
 */
export const OAuthCallback = () => {
  const { user, isLoading, refetchUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // On mount, extract session token from URL and store it
  useEffect(() => {
    const sessionToken = searchParams.get('session_token');
    if (sessionToken) {
      localStorage.setItem('session-token', sessionToken);
      // Refetch user now that we have the token
      refetchUser();
    } else if (!isLoading && !user) {
      // No token and no user - redirect to login
      navigate('/login', { replace: true });
    }
  }, [searchParams, refetchUser, isLoading, user, navigate]);

  useEffect(() => {
    // Wait for auth to load
    if (isLoading) return;

    // Only redirect once we have a user
    if (!user) return;

    // Check onboarding status and redirect
    if (!user.onboardingComplete) {
      navigate('/onboarding', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }, [user, isLoading, navigate]);

  return (
    <PageContainer>
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing sign-in...</p>
        </div>
      </div>
    </PageContainer>
  );
};
